import HeroSection from './components/HeroSection';
import WhatWeDoSection from './components/WhatWeDoSection';
import BenefitsSection from './components/BenefitsSection';
import IndustriesSection from './components/IndustriesSection';
import HowItWorksSection from './components/HowItWorksSection';
import TestimonialsSection from './components/TestimonialsSection';
import FaqSection from './components/FaqSection';
import CtaSection from './components/CtaSection';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://everythingautomation.ai';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Everything Automation',
  description: 'AI booking automation agency helping appointment-based businesses including hair salons, dental clinics, builders, trades, fitness studios, and home service providers fill their schedules automatically.',
  url: siteUrl,
  logo: 'https://static.readdy.ai/image/bcf9f2411fadd279f7d1fd38d7e2d7e8/6cfd408f22ccc1f303d260d7c847c128.png',
  email: 'sales@everythingautomation.co',
  telephone: '+44-7450-946360',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'GB',
  },
  knowsAbout: [
    'AI booking automation',
    'hair salon appointment systems',
    'dental clinic scheduling automation',
    'builders and trades lead management',
    'fitness studio booking automation',
    'home services scheduling',
  ],
  sameAs: [],
};

const webSiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Everything Automation',
  url: siteUrl,
  description: 'AI-powered booking automation for appointment-based businesses across the UK.',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${siteUrl}/?s={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How quickly will I start seeing more bookings?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Most clients see a noticeable uplift within the first 2 weeks. The biggest win comes from capturing enquiries that previously went unanswered outside business hours. Your AI responds instantly 24/7, so leads that used to go cold are converted before you even wake up.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does this work for my type of business?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. We specifically built our system for appointment-based businesses including hair salons, barbers, dental and medical clinics, builders and trades, fitness studios, physiotherapists, cleaners, and other home service providers. If your business runs on bookings, our automation is designed for you.',
      },
    },
    {
      '@type': 'Question',
      name: 'What happens when a client enquires outside business hours?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Your AI never clocks off. It responds instantly to every enquiry regardless of the time, answers questions about your services and pricing, and offers available booking slots. By the time you start your day, those enquiries are already confirmed appointments.',
      },
    },
    {
      '@type': 'Question',
      name: 'How does it handle booking changes and cancellations?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The AI manages rescheduling requests, updates your calendar, and sends new confirmation details automatically. For cancellations, it can offer the freed slot to other clients on a waiting list and recover the lost revenue without you doing a thing.',
      },
    },
    {
      '@type': 'Question',
      name: 'Will this replace my receptionist or front desk?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No - it handles the repetitive, time-consuming tasks: answering the same questions, taking bookings, sending reminders. Your team can then focus on the work that actually requires a human. Most clients find their staff are significantly less stressed after onboarding.',
      },
    },
    {
      '@type': 'Question',
      name: 'How long does setup take and do I need any technical knowledge?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Setup typically takes 30 to 60 days depending on the type and complexity of your automation. Simpler systems go live closer to 30 days. More complex builds can take up to 60 days to configure, test, and fully deploy. We handle everything. Zero technical knowledge required from you.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is there a contract or can I cancel?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'All plans are month-to-month with no long-term contracts. Cancel anytime with 30 days notice. We believe our results speak for themselves, and we would rather earn your business every month than lock you in.',
      },
    },
    {
      '@type': 'Question',
      name: 'What booking systems do you integrate with?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We connect with the tools you already use including Fresha, Treatwell, Calendly, Google Calendar, and many others. We will confirm compatibility with your specific setup during the onboarding call before you commit to anything.',
      },
    },
  ],
};

const HomePage = () => {
  return (
    <>
      <title>Everything Automation | AI Booking Automation for Hair Salons, Dentists, Trades &amp; More</title>
      <meta name="description" content="Fill your appointment book automatically with AI. Everything Automation helps hair salons, dental clinics, builders, trades, and fitness businesses capture more bookings 24/7. Book a free demo." />
      <meta name="keywords" content="AI booking automation, appointment scheduling automation, hair salon booking system, dental clinic automation, trades booking software" />
      <link rel="canonical" href={siteUrl} />
      <meta property="og:title" content="Everything Automation | AI Booking Automation for Appointment-Based Businesses" />
      <meta property="og:description" content="Fill your appointment book automatically. AI automation for hair salons, dental clinics, builders, trades, and fitness businesses. Never miss a booking again." />
      <meta property="og:url" content={siteUrl} />
      <meta property="og:type" content="website" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webSiteSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <main>
        <HeroSection />
        <WhatWeDoSection />
        <BenefitsSection />
        <IndustriesSection />
        <HowItWorksSection />
        <TestimonialsSection />
        <FaqSection />
        <CtaSection />
      </main>
    </>
  );
};

export default HomePage;